import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import {
  catchError,
  debounceTime,
  filter,
  map,
  mergeMap,
  retry,
  startWith,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs/operators';
import {
  combineLatest,
  EMPTY,
  forkJoin,
  from,
  fromEvent,
  of,
  Subscription,
  throwError,
  timer,
} from 'rxjs';
import {
  AuthActionsTypes,
  AuthorizeUser,
  AuthUserUpdate,
  DisplayInactiveTimeWarning,
  InactivityTimeoutLogout,
  LogIn,
  LogOut,
  RefreshTokenRequired,
  StartInactivityTime,
  StartRefreshTokenTime,
  StopInactivityTime,
  TokenUpdate,
} from 'src/app/auth/store/auth.actions';

import { MobileRoutesHelper } from '../../core/helpers/mobile-routes.helper';
import { Router } from '@angular/router';
import { CoreRoutes } from 'src/app/core/routes/core.routes';

import { environment } from 'src/environments/environment';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/reducers';
import { InactivityTimeDialogComponent } from 'src/app/auth/shared/inactivity-time-dialog/components/inactivity-time-dialog/inactivity-time-dialog.component';
import { ModalController } from '@ionic/angular/standalone';
import { MobileBottomDrawerService } from '../../shared/mobile-bottom-drawer/mobile-bottom-drawer.service';
import { MobileAuthService } from '../services/mobile-auth.service';
import { AuthUser } from '../../../../auth/models/auth-user.model';
import { StartFlowGlobalStore } from '../../../../start-flow/store/start-flow-global.store';
import { PreferencesService } from '../../biometrics/services/preferences.service';
import { ConnectivityService } from '../../shared/connectivity/connectivity.service';

// NOTE: Max time we wait for Auth0 `getTokenSilently()` before considering
// the silent refresh hung (e.g. app updated with stale refresh_token in
// localStorage, or flaky network). On timeout we fall through to
// loginWithRedirect so the user can re-authenticate instead of being stuck on
// the loading screen indefinitely.
const GET_TOKEN_SILENTLY_TIMEOUT_MS = 10_000;

// NOTE: Sentinel error thrown to abort a retry chain when the device is
// offline. Lets `catchError` decide whether to fall back to loginWithRedirect
// (online error) or simply stop and wait for connectivity (offline).
class OfflineAbortError extends Error {
  constructor() {
    super('Device is offline');
    this.name = 'OfflineAbortError';
  }
}

const isOfflineError = (err: unknown): boolean =>
  err instanceof OfflineAbortError ||
  (err as { name?: string } | null)?.name === 'OfflineAbortError';

@Injectable()
export class MobileAuthEffects {
  protected readonly actions$ = inject(Actions);
  protected readonly mobileAuthService = inject(MobileAuthService);
  protected readonly store = inject(Store<AppState>);
  protected readonly router = inject(Router);
  protected readonly modalController = inject(ModalController);
  protected readonly bottomDrawer = inject(MobileBottomDrawerService);
  protected readonly preferences = inject(PreferencesService);
  protected readonly connectivity = inject(ConnectivityService);
  protected readonly startFlowStore = inject(StartFlowGlobalStore);

  authorizeUser$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<AuthorizeUser>(AuthActionsTypes.AuthorizeUser),
        switchMap(() => {
          // NOTE: If we are offline, skip the silent token attempt entirely.
          // The offline banner already informs the user; when connectivity is
          // back (or they tap "Retry") we re-dispatch AuthorizeUser.
          if (this.connectivity.isOffline()) {
            console.warn(
              '[MobileAuthEffects] AuthorizeUser skipped: device is offline'
            );
            return EMPTY;
          }
          return this.mobileAuthService.getTokenSilently().pipe(
            take(1),
            timeout(GET_TOKEN_SILENTLY_TIMEOUT_MS),
            switchMap(token =>
              this.mobileAuthService.getAuth0User().pipe(
                // NOTE: user$ can emit null/stale right after a silent token
                // refresh; wait for a hydrated profile with a real `sub` so we
                // never dispatch LogIn with an undefined openId (which would
                // trigger a false user-switch and wipe the biometrics flags).
                filter(user => !!user?.sub),
                take(1),
                tap(user => {
                  this.store.dispatch(
                    new LogIn({ token, user: AuthUser.fromAuth0(user) })
                  );
                })
              )
            ),
            catchError(err => {
              // NOTE: If connectivity dropped during the silent attempt the
              // error is almost certainly network-related; don't bounce the
              // user through loginWithRedirect, which would just hang on a
              // closed browser. Wait for the user (or the cameOnline event)
              // to re-trigger AuthorizeUser.
              if (this.connectivity.isOffline()) {
                console.warn(
                  '[MobileAuthEffects] getTokenSilently failed while offline, waiting for connectivity'
                );
                return EMPTY;
              }
              console.warn(
                '[MobileAuthEffects] getTokenSilently failed, falling back to loginWithRedirect',
                (err as { name?: string } | null)?.name ?? err
              );
              return this.mobileAuthService.loginWithRedirect({
                cleanState: true,
              });
            })
          );
        }),
        catchError(error => {
          console.log('error', error);
          const queryParams = MobileRoutesHelper.getParams(false);
          this.router.navigate(
            [`${CoreRoutes.root}/${CoreRoutes.error}/true`],
            { queryParams }
          );
          return of(EMPTY);
        })
      ),
    { dispatch: false }
  );

  // NOTE: When connectivity is restored, silently re-dispatch AuthorizeUser
  // so any auth bootstrap that was skipped while offline can finally complete.
  // We only do this on the authorize route — refresh token flows fire on
  // their own next tick.
  retryAuthorizeOnConnectivity$ = createEffect(
    () =>
      this.connectivity.cameOnline$.pipe(
        tap(() => {
          if (this.router.url.includes('/auth/authorize')) {
            this.store.dispatch(new AuthorizeUser());
          }
        })
      ),
    { dispatch: false }
  );

  logout$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType<LogOut>(AuthActionsTypes.Logout),
        mergeMap(() => this.mobileAuthService.logOut())
      );
    },
    { dispatch: false }
  );
  login$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType<LogIn>(AuthActionsTypes.Login),
        switchMap(({ payload }) =>
          forkJoin([
            this.preferences.getOpenId(),
            of(payload.user.sub as string),
          ])
        ),
        switchMap(([savedOpenId, tokenOpenId]) => {
          // NOTE: Only wipe the trusted-device state when we can positively
          // identify a *different* user. A missing savedOpenId (first login /
          // cleared) or an empty tokenOpenId (user$ emitted before the profile
          // was hydrated) must NOT be treated as a user switch, otherwise we
          // drop `biometrics` + `loggedInDevice` for the same user and the
          // activate-biometrics-first-time prompt reappears on next boot.
          const isDifferentUser =
            !!savedOpenId && !!tokenOpenId && savedOpenId !== tokenOpenId;
          if (isDifferentUser) {
            return forkJoin([
              this.preferences.removeBiometricsPreferences(),
              this.preferences.removeLoggedInDevice(),
            ]).pipe(map(() => tokenOpenId));
          }
          return of(tokenOpenId);
        }),
        switchMap(tokenOpenId =>
          tokenOpenId ? this.preferences.setOpenId(tokenOpenId) : of(void 0)
        ),
        switchMap(() => this.preferences.removeLogOut()),
        tap(() => this.startFlowStore.launch())
      );
    },
    { dispatch: false }
  );
  tokenUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<TokenUpdate>(AuthActionsTypes.TokenUpdate),
      switchMap(() => this.mobileAuthService.getAuth0User().pipe(take(1))),
      map(user => new AuthUserUpdate({ user: AuthUser.fromAuth0(user) }))
    )
  );
  displayInactiveTimeWarning = createEffect(() =>
    this.actions$.pipe(
      ofType<DisplayInactiveTimeWarning>(
        AuthActionsTypes.DisplayInactiveTimeWarning
      ),
      switchMap(() =>
        from(
          this.bottomDrawer.openAndWait(InactivityTimeDialogComponent, {
            // NOTE: Critical security alert - block backdrop tap and swipe-dismiss to force explicit user decision
            backdropDismiss: false,
            canDismiss: false,
            // NOTE: Compact sheet sized for a 2-line confirmation, avoid full-screen modal on mobile
            initialBreakpoint: 0.35,
            breakpoints: [0, 0.35],
          })
        )
      ),
      map(() => new StartInactivityTime())
    )
  );

  // NOTE: Inactivity auto-timeout on mobile. We deliberately skip the Auth0
  // logout browser flow because the user has not interacted with the device
  // for a while: opening a Custom Tab without a user gesture races the
  // logout watchdog (10s) and frequently leaves the WebView stuck on the
  // authorize recovery screen. Instead we drop local Auth0 tokens and reload
  // — when the user comes back the bootstrap surfaces the regular
  // loginWithRedirect under their actual gesture (or biometrics, if
  // enabled).
  inactivityTimeoutLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<InactivityTimeoutLogout>(
          AuthActionsTypes.InactivityTimeoutLogout
        ),
        tap(() => {
          void this.mobileAuthService.localLogoutAndReload();
        })
      ),
    { dispatch: false }
  );
  startRefreshTokenTime$ = createEffect(() =>
    this.actions$.pipe(
      ofType<StartRefreshTokenTime>(AuthActionsTypes.StartRefreshTokenTime),
      debounceTime(1000 * environment.refreshTokenTime),
      switchMap(() => {
        // NOTE: Skip silent refresh while offline. Reschedules itself when the
        // device comes back online (see retryRefreshOnConnectivity$).
        if (this.connectivity.isOffline()) {
          console.warn(
            '[MobileAuthEffects] StartRefreshTokenTime skipped: device is offline'
          );
          return EMPTY;
        }
        return this.mobileAuthService.getTokenSilently().pipe(
          take(1),
          // NOTE: Abort the retry chain if the device goes offline mid-retry;
          // catchError below treats this as "wait, don't login redirect".
          retry({
            count: 10,
            delay: (_, retryCount) => {
              if (this.connectivity.isOffline()) {
                return throwError(() => new OfflineAbortError());
              }
              return timer(retryCount * 1000);
            },
          })
        );
      }),
      map(token => {
        this.store.dispatch(new TokenUpdate({ token }));
        return new StartRefreshTokenTime();
      }),
      catchError(error => {
        if (isOfflineError(error) || this.connectivity.isOffline()) {
          console.warn(
            '[MobileAuthEffects] StartRefreshTokenTime aborted: device went offline'
          );
          return EMPTY;
        }
        console.error({
          error,
          message:
            'getTokenSilently fails on auth effect - triggering clean login',
        });
        // NOTE: Use clean state login to minimize URL length after token refresh failure
        this.mobileAuthService
          .loginWithRedirect({ cleanState: true })
          .subscribe();
        return EMPTY;
      })
    )
  );
  refreshTokenRequired$ = createEffect(() =>
    this.actions$.pipe(
      ofType<RefreshTokenRequired>(AuthActionsTypes.RefreshTokenRequired),
      switchMap(() => {
        if (this.connectivity.isOffline()) {
          console.warn(
            '[MobileAuthEffects] RefreshTokenRequired skipped: device is offline'
          );
          return EMPTY;
        }
        return this.mobileAuthService.getTokenSilently().pipe(
          take(1),
          retry({
            count: 5,
            delay: (_, retryCount) => {
              if (this.connectivity.isOffline()) {
                return throwError(() => new OfflineAbortError());
              }
              return timer(retryCount * 1000);
            },
          })
        );
      }),
      map(token => new TokenUpdate({ token })),
      catchError(error => {
        if (isOfflineError(error) || this.connectivity.isOffline()) {
          console.warn(
            '[MobileAuthEffects] RefreshTokenRequired aborted: device went offline'
          );
          return EMPTY;
        }
        console.error({
          error,
          message: 'RefreshTokenRequired fails - triggering clean login',
        });
        this.mobileAuthService
          .loginWithRedirect({ cleanState: true })
          .subscribe();
        return EMPTY;
      })
    )
  );

  // NOTE: When the device comes back online, give the silent refresh pipeline
  // a chance to re-run so a logged-in user keeps a valid token after a flaky
  // network instead of waiting for the next scheduled tick.
  retryRefreshOnConnectivity$ = createEffect(() =>
    this.connectivity.cameOnline$.pipe(
      tap(() =>
        console.log(
          '[MobileAuthEffects] Connectivity restored, requesting refresh'
        )
      ),
      map(() => new StartRefreshTokenTime())
    )
  );
  private inactivitySubscription: Subscription;
  private inactivityTime$ = combineLatest([
    fromEvent(document, 'tap').pipe(startWith(null)),
    fromEvent(document, 'touchend').pipe(startWith(null)),
    fromEvent(document, 'touchmove').pipe(startWith(null)),
  ]).pipe(
    debounceTime(1000 * environment.inactivityTime),
    map(() => {
      this.store.dispatch(new DisplayInactiveTimeWarning());
      this.inactivitySubscription.unsubscribe();
    })
  );
  startInactivityTime$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<StartInactivityTime>(AuthActionsTypes.StartInactivityTime),
        tap(() => {
          if (this.inactivitySubscription) {
            this.inactivitySubscription.unsubscribe();
          }
          this.inactivitySubscription = new Subscription();
          this.inactivitySubscription.add(this.inactivityTime$.subscribe());
        })
      ),
    { dispatch: false }
  );

  stopInactivityTime$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<StopInactivityTime>(AuthActionsTypes.StopInactivityTime),
        tap(() => {
          console.log(
            'mobile: stop inactivity time: this.inactivitySubscription'
          );
          if (this.inactivitySubscription) {
            console.log(
              'mobile: stop inactivity time: this.inactivitySubscription.unsubscribe()'
            );
            this.inactivitySubscription.unsubscribe();
          }
        })
      ),
    { dispatch: false }
  );
}
